import { z } from "zod/v3";
import type { ContentBlock } from "./Chat.types";
import { UIComponent } from "../../types/uiComponent";

const uiComponentSchema = z.nativeEnum(UIComponent);

const textBlockSchema = z.object({
	type: z.literal("text"),
	text: z.string(),
	ui_component_type: uiComponentSchema,
});

const traceBlockSchema = z.object({
	type: z.literal("trace"),
	trace_type: z.string(),
	tool: z.string().optional(),
	content: z.string().optional(),
	success: z.boolean().optional(),
	summary: z.string().optional(),
	arguments: z.record(z.string(), z.unknown()).optional(),
	ui_component_type: uiComponentSchema,
});

const thinkingBlockSchema = z.object({
	type: z.literal("thinking"),
	text: z.string(),
	turn: z.number().optional(),
	block_id: z.string().optional(),
	ui_component_type: uiComponentSchema,
});

const chartBlockSchema = z.object({
	type: z.literal("chart"),
	version: z.number().int().positive(),
	chart_id: z.string().optional(),
	chart_type: z.string(),
	title: z.string().optional(),
	chart_data: z.record(z.string(), z.unknown()),
	ui_component_type: uiComponentSchema,
});

const tableOverflowPendingBlockSchema = z.object({
	type: z.literal("table_overflow_pending"),
	block_id: z.string(),
	revision: z.number().int().positive(),
	row_count: z.number().int().nonnegative(),
	filename: z.string(),
	ui_component_type: uiComponentSchema,
});

const tableOverflowBlockSchema = z.object({
	type: z.literal("table_overflow"),
	block_id: z.string(),
	revision: z.number().int().positive(),
	artifact_id: z.number().int().positive(),
	row_count: z.number().int().nonnegative(),
	filename: z.string(),
	ui_component_type: uiComponentSchema,
});

const chartOverflowPendingBlockSchema = z.object({
	type: z.literal("chart_overflow_pending"),
	block_id: z.string(),
	revision: z.number().int().positive(),
	filename: z.string(),
	size_bytes: z.number().int().nonnegative(),
	chart_type: z.string(),
	title: z.string().optional(),
	ui_component_type: uiComponentSchema,
});

const chartOverflowBlockSchema = z.object({
	type: z.literal("chart_overflow"),
	block_id: z.string(),
	revision: z.number().int().positive(),
	artifact_id: z.number().int().positive(),
	filename: z.string(),
	size_bytes: z.number().int().nonnegative(),
	chart_type: z.string(),
	title: z.string().optional(),
	ui_component_type: uiComponentSchema,
});

const modifiedWorkbookPreviewSchema = z.object({
	sheet: z.string(),
	rows: z.array(z.array(z.unknown())).default([]),
	row_count: z.number().int().nonnegative(),
	col_count: z.number().int().nonnegative(),
	truncated_cols: z.boolean().default(false),
});

const modifiedWorkbookBlockSchema = z.object({
	type: z.literal("modified_workbook"),
	block_id: z.string(),
	filename: z.string(),
	mime_type: z.string(),
	output_storage_uri: z.string(),
	download_signed_url: z.string().nullable().default(null),
	output_hash: z.string(),
	manifest_summary: z.record(z.string(), z.unknown()).default({}),
	recalc_status: z.string(),
	warnings: z.array(z.string()).default([]),
	formula_error_summary: z
		.array(z.record(z.string(), z.unknown()))
		.default([]),
	trace_id: z.string().nullable().default(null),
	duration_ms: z.number().int().nonnegative().default(0),
	preview: modifiedWorkbookPreviewSchema.nullable().default(null),
	hidden: z.boolean().optional(),
	ui_component_type: uiComponentSchema,
});

const schemaGraphNodeSchema = z.object({
	id: z.string(),
	name: z.string(),
	category: z.enum(["raw", "entity", "output"]),
	detail: z.string().optional(),
});

const schemaGraphEdgeSchema = z.object({
	source: z.string(),
	target: z.string(),
	label: z.string().optional(),
});

const schemaMapSchema = z.object({
	entities: z
		.array(
			z.object({
				name: z.string(),
				grain: z.string().default(""),
				grain_key: z.string().nullable().default(null),
				source_tab: z.string().nullable().default(null),
				example_tab: z.string().nullable().default(null),
				fields: z
					.array(
						z.object({
							name: z.string(),
							column: z.string().nullable().default(null),
							example_column: z.string().nullable().default(null),
						}),
					)
					.default([]),
			}),
		)
		.default([]),
	joins: z
		.array(
			z.object({
				source: z.string(),
				target: z.string(),
				on: z.string().default(""),
			}),
		)
		.default([]),
	source_tabs: z.array(z.string()).default([]),
	example_tabs: z.array(z.string()).default([]),
});

const schemaGraphBlockSchema = z.object({
	type: z.literal("schema_graph"),
	title: z.string().optional(),
	nodes: z.array(schemaGraphNodeSchema).default([]),
	edges: z.array(schemaGraphEdgeSchema).default([]),
	// Lenient: a schema section this build cannot read is a run without one,
	// not a block that fails to render at all.
	schema: schemaMapSchema.nullish().catch(null),
	sql: z.string().optional(),
	ui_component_type: uiComponentSchema,
});

const lineageSourceSchema = z.object({
	tab: z.string().default(""),
	column: z.string().default(""),
	canonical_field: z.string().default(""),
});

const columnLineageSchema = z.object({
	header: z.string(),
	kind: z.enum(["extracted", "derived"]).default("derived"),
	sql: z.string().default(""),
	transform: z.string().default(""),
	sources: z.array(lineageSourceSchema).default([]),
});

const loanTapePreviewColumnSchema = z.object({
	header: z.string(),
	align: z.enum(["left", "right"]).default("left"),
	lineage: columnLineageSchema.nullish(),
});

const loanTapePreviewBlockSchema = z.object({
	type: z.literal("loan_tape_preview"),
	filename: z.string().optional(),
	columns: z.array(loanTapePreviewColumnSchema).default([]),
	rows: z.array(z.array(z.string())).default([]),
	ui_component_type: uiComponentSchema,
});

const contentBlockSchema = z.discriminatedUnion("type", [
	textBlockSchema,
	traceBlockSchema,
	thinkingBlockSchema,
	chartBlockSchema,
	tableOverflowPendingBlockSchema,
	tableOverflowBlockSchema,
	chartOverflowPendingBlockSchema,
	chartOverflowBlockSchema,
	modifiedWorkbookBlockSchema,
	schemaGraphBlockSchema,
	loanTapePreviewBlockSchema,
]);

/**
 * Parse a raw object into a typed ContentBlock, returning null on failure.
 * Used in BOTH SSE parsing AND history hydration.
 */
export const parseContentBlock = (raw: unknown): ContentBlock | null => {
	const result = contentBlockSchema.safeParse(raw);
	return result.success ? (result.data as ContentBlock) : null;
};

export {
	chartBlockSchema,
	chartOverflowBlockSchema,
	chartOverflowPendingBlockSchema,
	contentBlockSchema,
	modifiedWorkbookBlockSchema,
	modifiedWorkbookPreviewSchema,
	tableOverflowBlockSchema,
	tableOverflowPendingBlockSchema,
};
